import {
  isHTMLElementsContainsTheTarget,
  isIgnoredElementOrChild,
  useOnClickOutside
} from 'hooks/useOnClickOutside'
import { RefObject, useCallback, useEffect, useState } from 'react'

interface IFocusableTags {
  [key: string]: string
}

const focusableTags: IFocusableTags = {
  INPUT: 'input',
  TEXTAREA: 'textarea'
}

interface IScrollDismissTooltipProps {
  hasAlertTooltip: boolean
  onClose: () => void
  scrollableContainerRef?: RefObject<HTMLElement | null>
  isAlertTooltipVisible: boolean
}

export const useScrollDismissTooltip = ({
  hasAlertTooltip,
  onClose,
  scrollableContainerRef,
  isAlertTooltipVisible
}: IScrollDismissTooltipProps) => {
  useEffect(() => {
    const container = scrollableContainerRef?.current
    if (!container) return

    const handleScroll = (event: Event) => {
      if (!hasAlertTooltip) {
        onClose()
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (!isAlertTooltipVisible) {
        onClose()
      }
    }

    const options = { passive: false }

    container.addEventListener('wheel', handleScroll, options)
    container.addEventListener('touchmove', handleScroll, options)

    return () => {
      container.removeEventListener('wheel', handleScroll)
      container.removeEventListener('touchmove', handleScroll)
    }
  }, [hasAlertTooltip, isAlertTooltipVisible, onClose, scrollableContainerRef])
}

export const useAlertTooltip = (onClose: () => void) => {
  const [isVisible, setIsVisible] = useState(false)

  const toggleTooltip = useCallback(() => {
    if (isVisible) {
      setIsVisible(false)
      onClose()
    } else {
      setIsVisible(true)
    }
  }, [onClose, isVisible])

  return { isVisible, toggleTooltip }
}

export const useTtsFlyoutVisibility = (
  onClose: () => void,
  hasAlertTooltip: boolean,
  scrollableContainerRef?: RefObject<HTMLElement | null>
) => {
  const { isVisible, toggleTooltip } = useAlertTooltip(onClose)

  const handleClose = useCallback(() => {
    if (hasAlertTooltip) {
      toggleTooltip()
    } else {
      onClose()
    }
  }, [hasAlertTooltip, toggleTooltip, onClose])

  useScrollDismissTooltip({
    hasAlertTooltip,
    onClose: handleClose,
    scrollableContainerRef,
    isAlertTooltipVisible: isVisible
  })

  return { handleClose, isAlertTooltipVisible: isVisible }
}

//when flyout is open and tts item is changed, we dont allow to close the flyout at first time,
//we show alert tooltip and block all the other events except the click on the flyout
export function useBlockingClickOutside({
  refs,
  handler,
  ignoredClassNames,
  containerId,
  preventOutsideInteraction
}: {
  refs: Array<HTMLElement | SVGElement | null> | null
  handler: (event: Event) => unknown
  ignoredClassNames?: Array<string>
  containerId?: string
  preventOutsideInteraction?: boolean
}): void {
  const container =
    (containerId && document.getElementById(containerId)) || document

  //we use the base click outside hook for handling clicks
  useOnClickOutside(refs, handler, ignoredClassNames, containerId)

  useEffect(() => {
    if (!preventOutsideInteraction) return

    const clickHandler = (event: Event) => {
      const isRefsContainsTheTarget =
        !refs ||
        (event.target instanceof Node &&
          isHTMLElementsContainsTheTarget(refs, event.target))

      const ignoredClassNamesCondition =
        ignoredClassNames &&
        event.target instanceof Element &&
        isIgnoredElementOrChild(event.target, ignoredClassNames)

      if (
        !isRefsContainsTheTarget &&
        !ignoredClassNamesCondition &&
        preventOutsideInteraction
      ) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()

        // Manually remove focus from interactive form elements.
        // This is necessary because preventDefault() alone does not stop the browser
        // from moving focus to elements like <input> or <textarea> on click.
        if (
          event.target instanceof HTMLElement &&
          focusableTags[event.target.tagName]
        ) {
          event.target.blur()
        }
      }
    }

    container.addEventListener('click', clickHandler, true)

    return () => {
      container.removeEventListener('click', clickHandler, true)
    }
  }, [refs, ignoredClassNames, preventOutsideInteraction, container])
}
